/**
 * AutoPilotShell — the shared AP chrome.
 *
 * Hero bar (top, full width) + workspace (left, fills) + right rail
 * (chat on top, Verlauf on bottom). The caller passes in the active AP
 * definition; the shell stays AP-agnostic.
 *
 * v3: provides APContext, passes apId to ChatRail, and clears any
 * in-flight diff state when the active AP changes.
 */

import clsx from "clsx"
import { useEffect, useState } from "react"
import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import { useStore } from "../../store/useStore"
import type { AutoPilotDef } from "./registry"
import { APContext } from "./shared/APContext"
import { APSettingsPage } from "./shared/APSettingsPage"
import { ChatRail } from "./shared/ChatRail"
import { HeroBar } from "./shared/HeroBar"
import { SettingsButton } from "./shared/SettingsButton"
import { VerlaufRail } from "./shared/VerlaufRail"

type TabId = "aktionen" | "datenbank" | "einstellungen"

interface Props {
  ap: AutoPilotDef
  onBack?: () => void
}

export function AutoPilotShell({ ap, onBack }: Props) {
  const {
    dbConfigured,
    scopes,
    actionState,
    exitDiffMode,
    setActiveDiffSessionId,
    setActiveScopeId,
  } = useStore()
  const [activeTab, setActiveTab] = useState<TabId>("aktionen")

  // Clear diff state when AP changes so there is never concurrent diff state across APs.
  // Also pin Datenbank to the AP's primary scope so importer-AP selection mode
  // lands on the right scope tab from first entry.
  useEffect(() => {
    exitDiffMode()
    setActiveDiffSessionId(null)
    setActiveTab("aktionen")
    if (ap.scope.primaryScopeId) {
      setActiveScopeId(ap.scope.primaryScopeId)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ap.id])

  const hero = ap.hero({
    dbConfigured,
    scopes,
    hasActiveDiffSession: !!actionState,
  })

  const handleSettingsToggle = () => {
    setActiveTab((prev) =>
      prev === "einstellungen" ? "aktionen" : "einstellungen",
    )
  }

  return (
    <APContext.Provider value={ap}>
      <div className="flex h-full flex-col bg-grey2">
        <HeroBar text={hero} onBack={onBack} />

        <div className="flex flex-1 overflow-hidden">
          {/* Workspace */}
          <div className="flex flex-1 flex-col overflow-hidden">
            <TabBar
              activeTab={activeTab}
              onTab={setActiveTab}
              onSettings={handleSettingsToggle}
            />
            <div className="flex-1 overflow-hidden">
              {/* Keep tabs mounted to preserve scroll + transient UI state. */}
              <div
                className={clsx(
                  "h-full",
                  activeTab === "aktionen" ? "" : "hidden",
                )}
              >
                <ap.AktionenTab />
              </div>
              <div
                className={clsx(
                  "h-full",
                  activeTab === "datenbank" ? "" : "hidden",
                )}
              >
                <ap.DatenbankTab />
              </div>
              <div
                className={clsx(
                  "h-full",
                  activeTab === "einstellungen" ? "" : "hidden",
                )}
              >
                <APSettingsPage ap={ap} />
              </div>
            </div>
          </div>

          {/* Right rail */}
          <div className="flex w-[380px] shrink-0 flex-col border-l border-grey4 bg-grey1">
            <div className="flex-1 overflow-hidden">
              <ChatRail apId={ap.id} />
            </div>
            <VerlaufRail />
          </div>
        </div>
      </div>
    </APContext.Provider>
  )
}

function TabBar({
  activeTab,
  onTab,
  onSettings,
}: {
  activeTab: TabId
  onTab: (t: TabId) => void
  onSettings: () => void
}) {
  const { translate } = useTranslation()
  return (
    <div className="flex items-center gap-1 border-b border-grey4 bg-grey1 px-4">
      <TabButton
        active={activeTab === "aktionen"}
        onClick={() => onTab("aktionen")}
      >
        {translate('QMS/AUTOPILOT/ACTIONS_TAB')}
      </TabButton>
      <TabButton
        active={activeTab === "datenbank"}
        onClick={() => onTab("datenbank")}
      >
        {translate('QMS/AUTOPILOT/DATABASE_TAB')}
      </TabButton>
      <SettingsButton
        active={activeTab === "einstellungen"}
        onClick={onSettings}
      />
    </div>
  )
}

function TabButton({
  active,
  onClick,
  children,
}: {
  active: boolean
  onClick: () => void
  children: React.ReactNode
}) {
  return (
    <button
      onClick={onClick}
      className={clsx(
        "border-b-2 px-4 py-2.5 text-sm transition-colors",
        active
          ? "border-yellow1 text-white"
          : "border-transparent text-grey5 hover:text-white",
      )}
    >
      {children}
    </button>
  )
}
